import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authService } from '../../services/auth.service';
import { userService } from '../../services/user.service';
import { taskService } from '../../services/task.service';
import { leaveService } from '../../services/leave.service';
import { attendanceService } from '../../services/attendance.service';
import { FaUsers, FaTasks, FaCalendarAlt, FaChartLine, FaClock } from 'react-icons/fa';

const ManagerDashboard = () => {
  const user = authService.getCurrentUser();
  const [stats, setStats] = useState({
    teamMembers: 0,
    pendingLeaves: 0,
    activeTasks: 0,
    todayPresent: 0,
    todayAbsent: 0
  });
  const [teamTasks, setTeamTasks] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchManagerData();
  }, []);

  const fetchManagerData = async () => {
    try {
      setLoading(true);

      // Fetch team members
      const usersData = await userService.getAllUsers({ status: 'active' }).catch(() => ({ users: [] }));
      const teamMembers = usersData.users || [];

      // Fetch all tasks
      const tasksData = await taskService.getAllTasks().catch(() => ({ tasks: [] }));
      const allTasks = tasksData.tasks || [];
      
      // Filter tasks assigned to team members
      const activeTasks = allTasks.filter(t => t.status !== 'completed');

      // Fetch pending leaves
      const leavesData = await leaveService.getAllLeaves({ status: 'pending' }).catch(() => ({ leaves: [] }));
      const pendingLeavesList = leavesData.leaves || [];

      // Fetch today's attendance
      const attendanceData = await attendanceService.getAttendanceReport({
        date: new Date().toISOString().split('T')[0]
      }).catch(() => ({ attendance: [] }));

      const todayAttendance = attendanceData.attendance || [];
      const presentCount = todayAttendance.filter(a => a.status === 'present' || a.status === 'late').length;
      const absentCount = teamMembers.length - presentCount;

      setStats({
        teamMembers: teamMembers.length,
        pendingLeaves: pendingLeavesList.length,
        activeTasks: activeTasks.length,
        todayPresent: presentCount,
        todayAbsent: absentCount
      });

      setTeamTasks(activeTasks.slice(0, 5)); // Show top 5 tasks
      setPendingLeaves(pendingLeavesList.slice(0, 5)); // Show top 5 pending leaves
    } catch (error) {
      console.error('Error fetching manager data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveLeave = async (leaveId) => {
    try {
      await leaveService.approveLeave(leaveId, 'Approved by manager');
      toast.success('Leave approved successfully!');
      fetchManagerData();
    } catch (error) {
      toast.error('Failed to approve leave');
    }
  };

  const handleRejectLeave = async (leaveId) => {
    try {
      await leaveService.rejectLeave(leaveId, 'Rejected by manager');
      toast.success('Leave rejected');
      fetchManagerData();
    } catch (error) {
      toast.error('Failed to reject leave');
    }
  };

  const menuItems = [
    { name: 'Team Members', icon: FaUsers, path: '/dashboard/manager/team', color: 'blue' },
    { name: 'Team Tasks', icon: FaTasks, path: '/dashboard/manager/tasks', color: 'green' },
    { name: 'Leave Requests', icon: FaCalendarAlt, path: '/dashboard/manager/leaves', color: 'yellow' },
    { name: 'Team Attendance', icon: FaClock, path: '/dashboard/manager/attendance', color: 'purple' },
  ];

  return (
    <div className="p-6">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Manager Dashboard</h1>
        <p className="text-gray-600 mt-2">Welcome, {user?.name} - Manage your team effectively</p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Team Members</p>
                  <p className="text-3xl font-bold text-blue-600 mt-2">{stats.teamMembers}</p>
                </div>
                <FaUsers className="text-4xl text-blue-200" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Active Tasks</p>
                  <p className="text-3xl font-bold text-green-600 mt-2">{stats.activeTasks}</p>
                </div>
                <FaTasks className="text-4xl text-green-200" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Pending Leaves</p>
                  <p className="text-3xl font-bold text-yellow-600 mt-2">{stats.pendingLeaves}</p>
                </div>
                <FaCalendarAlt className="text-4xl text-yellow-200" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Today Present</p>
                  <p className="text-3xl font-bold text-green-600 mt-2">{stats.todayPresent}</p>
                </div>
                <FaClock className="text-4xl text-green-200" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Today Absent</p>
                  <p className="text-3xl font-bold text-red-600 mt-2">{stats.todayAbsent}</p>
                </div>
                <FaClock className="text-4xl text-red-200" />
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h2 className="text-xl font-bold mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="flex flex-col items-center p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
                >
                  <item.icon className="text-3xl text-blue-600 mb-2" />
                  <span className="text-sm font-medium text-center">{item.name}</span>
                </Link>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pending Leave Requests */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold">Pending Leave Requests</h2>
                  <Link to="/dashboard/manager/leaves" className="text-sm text-blue-600 hover:text-blue-800">
                    View All
                  </Link>
                </div>
              </div>
              <div className="p-6">
                {pendingLeaves.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No pending leave requests</p>
                ) : (
                  <div className="space-y-4">
                    {pendingLeaves.map((leave) => (
                      <div key={leave._id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-semibold">{leave.user?.name || 'Unknown'}</p>
                            <p className="text-sm text-gray-600 capitalize">{leave.type} Leave</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(leave.startDate).toLocaleDateString()} - {new Date(leave.endDate).toLocaleDateString()} ({leave.totalDays} days)
                            </p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 mb-3">{leave.reason}</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApproveLeave(leave._id)}
                            className="flex-1 bg-green-600 text-white py-1 px-3 rounded text-sm hover:bg-green-700"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleRejectLeave(leave._id)}
                            className="flex-1 bg-red-600 text-white py-1 px-3 rounded text-sm hover:bg-red-700"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Active Team Tasks */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold">Active Team Tasks</h2>
                  <Link to="/dashboard/manager/tasks" className="text-sm text-blue-600 hover:text-blue-800">
                    View All
                  </Link>
                </div>
              </div>
              <div className="p-6">
                {teamTasks.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No active tasks</p>
                ) : (
                  <div className="space-y-4">
                    {teamTasks.map((task) => (
                      <div key={task._id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <p className="font-semibold">{task.title}</p>
                            <p className="text-sm text-gray-600">Assigned to: {task.assignedTo?.name || 'Unassigned'}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Due: {new Date(task.dueDate).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex flex-col gap-1 items-end">
                            <span className={`px-2 py-1 text-xs rounded ${
                              task.priority === 'high' ? 'bg-red-100 text-red-800' :
                              task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {task.priority}
                            </span>
                            <span className={`px-2 py-1 text-xs rounded ${
                              task.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {task.status.replace('_', ' ')}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700">{task.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ManagerDashboard;
